/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './LOVField';
import { analyzeLOVField } from './LOVFieldAnalyze';

const LOVFieldManager: ComponentManagerFn<'LOVField'> = (
  platform: Platform,
  type: 'LOVField',
): ComponentManager<'LOVField'> => {
  return {
    ...baseComponentManager<'LOVField'>(platform, 'LOVField'),
    type: 'LOVField',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'TriggerOnChange',
            'LOVGrid',
            'LOVMappings',
            'TriggerAfterServerValidate',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      const children = platform.getChildrenOfType(
        appUid,
        pageId,
        page,
        item,
        childType,
      );
      return children.length === 0 ? true : 'Already added';
    },
    defaultChildren: ['LOVGrid', 'LOVMappings'],
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => [
          'Email',
          'Phone',
          'Reference',
          'String',
          'UserID',
        ],
      },
      comboDatasourceAlias: {
        label: 'LOV Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['ReadOnlyDataStore'],
        required: true,
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Data',
        helpText: 'Enable this to prevent blank query on LOV open',
      },
      returnValueAttribute: {
        label: 'Return Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      popupWidth: {
        label: 'Popup Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Popup Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      showGridFilters: {
        label: 'Show Grid Filters',
        type: 'boolean',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      allowInvalidValue: {
        label: 'Allow Invalid Value',
        type: 'boolean',
        category: 'Data',
      },
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
      defaultFilterFocusColumn: {
        label: 'Default Filter Focus Column',
        type: 'ItemID',
        helpText:
          'Controls default filter focus. If a column Id is provided, the corresponding filter input will be focused when the popup opens.',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          const parentItem = platform.getItem(
            appUid,
            pageId,
            item?.parent || '',
          );

          if (parentItem?.type !== 'LOVGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      popupCssClassName: {
        label: 'Lov Popup CSS Class Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Look & Feel',
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'returnValueAttribute':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'open':
          return false;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    analyze: analyzeLOVField,
    name: 'LOV Field',
    devStyles: { display: 'inline-flex' },
    category: 'Forms',
    icon: { iconName: 'io-lov-field', iconType: 'cloudio' },
    excludeChildren: true,
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The LOV (List of Values) Field is a sophisticated input component used for selecting a single value from a large dataset. Unlike a standard dropdown (Select) which only shows a list of strings, the LOV Field opens a modal popup containing a full data grid. This allows users to search, filter, and view multiple columns of information before making a selection.
Use this component when users need to select a record (like a Customer, Product, or Part Number) where seeing additional context (like Address, Price, or Description) is necessary to pick the correct item.

**1. Data Source Configuration**<br/>
The LOV Field requires a connection to a secondary data source that supplies the list of options.
* **LOV Data Store Alias (comboDatasourceAlias):** Selects the **ReadOnlyDataStore** that contains the master list of records.
* **Return Value Attribute:** Specifies which specific column from the LOV Data Store should be saved when a row is selected.
* **Do Not Prefetch:** A boolean setting. If enabled, the component will not load the data list until the user actually clicks to open the popup, improving initial page load performance.

**2. Data Binding**<br/>
On the form side, the component binds to a field to store the result of the selection.
* **View Attribute:** The field in your main form where the selected value is stored.
* **Supported Types:** String, Reference, Email, Phone, UserID.
* **Allow Invalid Value:** If checked, allows the user to manually type a value that does not exist in the LOV list.
* **Server Validate On Change:** Triggers immediate server-side validation logic upon selection.

**3. Popup Customization**<br/>
You can control the physical appearance and behavior of the selection modal.
* **Dimensions:** You can define the **Popup Width** (400px–1200px) and **Popup Height** (200px–800px) to ensure the grid fits comfortably on the screen.
* **Show Grid Filters:** Enables search headers within the popup grid, allowing users to filter the list (e.g., searching by "Last Name").

**4. Child Components & Mapping**<br/>
The LOV Field relies on specific child components to function correctly.
* **LOV Grid:** You must add columns as children to this component. These columns define what data is visible inside the popup table.
* **LOV Mappings:** This allows for complex data transfer. When a user selects a row (e.g., a "Product"), this component can automatically fill **other** fields on the form (e.g., "Unit Price" and "Description") using data from the selected row.

**5. Best Use Cases**<br/>
* **Customer Selection:** A sales form where the user searches for a customer by name, but needs to see the City and Account Number to ensure they pick the right one.
* **Inventory Management:** Selecting a part number from a catalog of thousands, where filtering by "Category" or "Manufacturer" is required.
* **Employee Lookup:** Assigning a task to a user, where the popup displays Department and Job Title context.`,
  };
};

export default LOVFieldManager;
